import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ParentLayout } from "@/components/layout/ParentLayout";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Checkbox";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { IconButton } from "@/components/ui/IconButton";
import { Pill } from "@/components/ui/Pill";
import { useObservable } from "@/hooks/useObservable";
import { getCurrentProfile } from "@/lib/profile";
import { familyService } from "@/services/family.service";
import { jobsService } from "@/services/jobs.service";
import type { JobData, JobRecord, ScreenBand } from "@/types/domain";

const BLANK: Omit<JobData, "sort_order"> = {
  emoji: "📱",
  title: "",
  price: null,
  max_per_day: null,
  max_per_week: null,
  kind: "screen",
  bands: [],
  require_comment: false,
  comment_label: null,
  requires_approval: true,
  assigned_to: [],
  is_active: true,
};

export function ParentRewards() {
  const jobs = useObservable(jobsService.jobs$, []);
  const familyState = useObservable(familyService.state$, {
    members: [],
    settings: null,
    loading: false,
  });
  const children = familyState.members.filter((m) => m.data.role === "child");

  const [editing, setEditing] = useState<JobRecord | "new" | null>(null);
  const [form, setForm] = useState<Omit<JobData, "sort_order">>(BLANK);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<JobRecord | null>(null);

  const rewards = jobs.filter((j) => j.data.kind === "screen");

  function openNew() {
    setForm({ ...BLANK, assigned_to: children.map((c) => c.id) });
    setEditing("new");
  }

  function openEdit(job: JobRecord) {
    setForm({ ...BLANK, ...job.data });
    setEditing(job);
  }

  async function handleSave() {
    if (!form.title) {
      setFormError("Belønningen må ha et navn.");
      return;
    }
    if (!form.bands?.length) {
      setFormError("Belønningen må ha minst ett trinn.");
      return;
    }
    if (children.length > 0 && form.assigned_to.length === 0) {
      setFormError("Belønningen må tildeles minst ett barn.");
      return;
    }
    setFormError(null);
    if (saving) return;
    setSaving(true);
    try {
      const data: JobData = {
        ...form,
        sort_order:
          editing === "new"
            ? rewards.length
            : (editing as JobRecord).data.sort_order,
      };
      if (editing === "new") {
        const familyId = getCurrentProfile().profile!.data.family_id;
        await jobsService.createJob(familyId, data);
      } else {
        await jobsService.updateJob((editing as JobRecord).id, data);
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    await jobsService.archiveJob(pendingDelete.id);
    setPendingDelete(null);
  }

  return (
    <>
      <ParentLayout>
        <div style={{ maxWidth: 680 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: 28,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--ink-3)",
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  fontWeight: 700,
                }}
              >
                Administrasjon
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: 34,
                  fontWeight: 400,
                  marginTop: 2,
                  letterSpacing: -0.8,
                }}
              >
                Belønninger
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={15} color="#fff" />}
              onClick={openNew}
              data-testid="new-reward-button"
            >
              Ny belønning
            </Button>
          </div>

          {editing && (
            <Card
              style={{
                padding: 20,
                marginBottom: 20,
                border: "2px solid var(--forest)",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
                {editing === "new" ? "Ny belønning" : "Rediger belønning"}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <Field label="Emoji">
                  <input
                    data-testid="reward-emoji-input"
                    value={form.emoji}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, emoji: e.target.value }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <Field label="Navn">
                  <input
                    data-testid="reward-title-input"
                    value={form.title}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, title: e.target.value }))
                    }
                    style={inputStyle}
                    placeholder="f.eks. Skjermtid"
                  />
                </Field>
              </div>

              <BandsEditor
                bands={form.bands ?? []}
                onChange={(bands) => setForm((f) => ({ ...f, bands }))}
              />

              {children.length > 0 && (
                <Field label="Tildelt til" style={{ marginTop: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 4,
                    }}
                  >
                    {children.map((c) => (
                      <Checkbox
                        key={c.id}
                        data-testid="child-checkbox"
                        checked={form.assigned_to.includes(c.id)}
                        onChange={(checked) =>
                          setForm((f) => ({
                            ...f,
                            assigned_to: checked
                              ? [...f.assigned_to, c.id]
                              : f.assigned_to.filter((id) => id !== c.id),
                          }))
                        }
                      >
                        {c.data.name}
                      </Checkbox>
                    ))}
                  </div>
                </Field>
              )}

              {formError && (
                <div
                  data-testid="form-error"
                  style={{ color: "var(--bad)", fontSize: 13, marginTop: 12 }}
                >
                  {formError}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Check size={14} color="#fff" />}
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-reward-button"
                >
                  {saving ? "Lagrer…" : "Lagre"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(null)}
                >
                  Avbryt
                </Button>
              </div>
            </Card>
          )}

          {rewards.length > 0 && (
            <Card padding={0} style={{ overflow: "hidden" }}>
              {rewards.map((j, i) => (
                <div
                  key={j.id}
                  data-testid="reward-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderBottom:
                      i < rewards.length - 1 ? "1px solid var(--line)" : "none",
                  }}
                >
                  <div style={{ fontSize: 22, width: 36, textAlign: "center" }}>
                    {j.data.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {j.data.title}
                    </div>
                    <div
                      className="num"
                      style={{ fontSize: 12, color: "var(--ink-3)" }}
                    >
                      {j.data.bands?.length
                        ? `${j.data.bands.length} trinn`
                        : "Ingen trinn"}
                    </div>
                  </div>
                  {children
                    .filter((c) => j.data.assigned_to.includes(c.id))
                    .map((c) => (
                      <div
                        key={c.id}
                        data-testid="child-avatar"
                        title={c.data.name}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: c.data.color,
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {c.data.initials}
                      </div>
                    ))}
                  {!j.data.is_active && (
                    <Pill tone="warn" size="sm">
                      Inaktiv
                    </Pill>
                  )}
                  <div style={{ display: "flex", gap: 2 }}>
                    <IconButton
                      data-testid="edit-reward-button"
                      onClick={() => openEdit(j)}
                    >
                      <Pencil size={24} color="currentColor" />
                    </IconButton>
                    <IconButton
                      data-testid="delete-reward-button"
                      tone="danger"
                      onClick={() => setPendingDelete(j)}
                    >
                      <Trash2 size={24} color="currentColor" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </ParentLayout>
      {pendingDelete && (
        <ConfirmDialog
          title="Slett belønning?"
          message={`«${pendingDelete.data.title}» blir slettet permanent.`}
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </>
  );
}

function BandsEditor({
  bands,
  onChange,
}: {
  bands: ScreenBand[];
  onChange: (bands: ScreenBand[]) => void;
}) {
  function addBand() {
    const lastMax = bands.at(-1)?.max ?? 0;
    onChange([...bands, { max: lastMax + 3.5, label: "", amount: 0 }]);
  }

  function updateBand(i: number, patch: Partial<ScreenBand>) {
    onChange(bands.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  }

  function removeBand(i: number) {
    onChange(bands.filter((_, idx) => idx !== i));
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--ink-3)",
          marginBottom: 8,
        }}
      >
        Belønningstrinn
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {bands.map((band, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: bands are positional; updateBand/removeBand operate by index
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              gap: 8,
              alignItems: "center",
            }}
          >
            <Field label={i === 0 ? "Maks timer" : ""}>
              <input
                type="number"
                value={band.max}
                min="0"
                step="0.5"
                onChange={(e) => updateBand(i, { max: Number(e.target.value) })}
                style={inputStyle}
                placeholder="t.eks. 3.5"
              />
            </Field>
            <Field label={i === 0 ? "Beløp (kr)" : ""}>
              <input
                data-testid="band-amount-input"
                type="number"
                value={band.amount}
                onChange={(e) =>
                  updateBand(i, { amount: Number(e.target.value) })
                }
                style={inputStyle}
                placeholder="t.eks. 50 eller -50"
              />
            </Field>
            <button
              type="button"
              onClick={() => removeBand(i)}
              style={{
                background: "none",
                color: "var(--bad)",
                padding: "0 4px",
                alignSelf: "flex-end",
                height: 38,
              }}
            >
              <Trash2 size={15} color="currentColor" />
            </button>
          </div>
        ))}
      </div>
      <button
        data-testid="add-band-button"
        type="button"
        onClick={addBand}
        style={{
          marginTop: 8,
          fontSize: 13,
          color: "var(--forest)",
          fontWeight: 600,
          background: "none",
        }}
      >
        + Legg til trinn
      </button>
      <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
        Siste trinn gjelder for alt over forrige grense. Beløp kan være
        negativt.
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  style,
}: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={style}>
      {label && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "var(--ink-3)",
            marginBottom: 4,
          }}
        >
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 38,
  padding: "0 10px",
  border: "1px solid var(--line-2)",
  borderRadius: 8,
  fontSize: 14,
  background: "var(--bg)",
  boxSizing: "border-box",
};
